import React from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';

import {
  FormControl,
  Stack,
  TextField,
  Button,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import MUIDataTable from 'mui-datatables';

import reportActions from '../../../actions/reports';
import WrappedSpinner from '../../common/wrapped-spinner';
import { MarketSelect } from '../../common/market-select';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import BatteryLevel from '../../common/battery-level';
import { longDateTimeFormat } from '../../../lib/utils';
import saveLocalSettingsWrapper from './save-report-settings';

const LOCAL_STORAGE_KEY = 'inventory-report';
const styles = {
  root: {
    width: '95%',
    height: '95%',
    margin: '0 auto',
    overflowX: 'auto',
  },
  formControl: {
    width: '100%',
    left: '10px',
  },
};

function showVehicleStatus(statusList) {
  if (statusList) {
    return (
      <Grid>
        {statusList.map(
          (status) => status && <Typography key={status}>{status}</Typography>,
        )}
      </Grid>
    );
  } else {
    return '';
  }
}

function getVehicleStatus(event) {
  const statuses = [];
  statuses.push(event.vehicleExtStatus);
  statuses.push(event.vehicleExtDeploymentStatus);
  statuses.push(event.vehicleExtPossessionStatus);

  return statuses;
}

function getPreviousVehicleStatus(event) {
  const statuses = [];
  statuses.push(event.vehiclePreviousExtStatus);
  statuses.push(event.vehiclePreviousExtDeploymentStatus);
  statuses.push(event.vehiclePreviousExtPossessionStatus);

  return statuses;
}

function formatTableData(events) {
  return events.map((event) => [
    get(event, 'vehicle.humanId', ''),
    get(event, 'market.name', ''),
    getPreviousVehicleStatus(event),
    getVehicleStatus(event),
    `${get(event, 'wrangler.firstName', '')} ${get(event, 'wrangler.lastName', '')}`,
    { date: event.createdAt, tz: get(event, 'market.tz', 'America/New_York') },
    event.batteryLevel,
  ]);
}

function addDatesToQuery(query, startDate, endDate) {
  return {
    ...query,
    start_date: moment(startDate).format('x'),
    end_date: moment(endDate).format('x'),
  };
}

class BaseInventoryReport extends React.Component {
  constructor(props) {
    super(props);

    const {
      marketId, startDate, endDate,
    } = props;

    this.state = {
      marketId: marketId || '',
      startDate: startDate
        ? moment.utc(startDate).format('yyyy-MM-DDTHH:mm')
        : moment.utc().format('yyyy-MM-DDT00:00'),
      endDate: endDate
        ? moment.utc(endDate).format('yyyy-MM-DDTHH:mm')
        : moment.utc().format('yyyy-MM-DDT23:59'),
    };

    this.handleChange = this.handleChange.bind(this);
    this.fetchData = this.fetchData.bind(this);
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps, prevState) {
    const { saveReportSettings } = this.props;
    const {
      startDate,
      endDate,
      marketId,
    } = this.state;

    if (isUndefined(startDate) || isUndefined(endDate)) return;
    const isMarketChanged = marketId !== prevState.marketId;
    const isSameStartDate = moment(prevState.startDate).isSame(startDate);
    const isSameEndDate = moment(prevState.endDate).isSame(endDate);
    const isDateChanged = !isSameStartDate || !isSameEndDate;

    if (isMarketChanged || isDateChanged) {
      saveReportSettings({
        marketId,
        startDate,
        endDate,
      });

      if (isMarketChanged) {
        this.fetchData();
      }
    }
  }

  get tableOptions() {
    return this.props.tableOptions;
  }

  get columns() {
    const { selectedFilters, primarySort } = this.props;
    const columnData = [
      { name: 'Vehicle ID' },
      { name: 'Market' },
      { name: 'Previous State/Status', options: { customBodyRender: (previousStatusList) => showVehicleStatus(previousStatusList) } },
      { name: 'State/Status', options: { customBodyRender: (statusList) => showVehicleStatus(statusList) } },
      { name: 'Wrangler' },
      {
        name: 'Timestamp',
        options: {
          customBodyRender: (data) => moment.utc(data.date).tz(data.tz).format(longDateTimeFormat),
          sortCompare: (order) => (obj1, obj2) => (obj1.data.date.localeCompare(obj2.data.date)) * (order === 'asc' ? 1 : -1),
        },
      },
      { name: 'Battery Level', options: { customBodyRender: (batteryLevel) => (batteryLevel ? <BatteryLevel batteryLevel={batteryLevel} /> : <></>) } },
    ];
    return this.props.addToColumnData(columnData, selectedFilters, primarySort);
  }

  fetchData() {
    const { fetchInventoryReport } = this.props;
    const {
      marketId, startDate, endDate,
    } = this.state;

    const query = addDatesToQuery({}, startDate, endDate);
    if (marketId) query.market_id = marketId;
    fetchInventoryReport(query);
  }

  handleChange(event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  render() {
    const {
      marketsLoading, reportsLoading, inventoryEvents, getTitleFromData,
    } = this.props;
    const {
      startDate,
      endDate,
      marketId,
    } = this.state;
    const isLoading = marketsLoading || reportsLoading;
    return (
      <WrappedSpinner isLoading={isLoading}>
        <Paper style={styles.root} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={3}>
              <MarketSelect
                name="marketId"
                value={marketId || ''}
                handleChange={(newMarketId) => this.setState({ marketId: newMarketId })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl
                component="fieldset"
                style={styles.formControl}
              >
                <Stack direction="row" spacing={1} component="form">
                  <TextField
                    label="Date From"
                    type="datetime-local"
                    name="startDate"
                    value={startDate}
                    sx={{ width: 200 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={this.handleChange}
                  />
                  <TextField
                    label="Date To"
                    type="datetime-local"
                    value={endDate}
                    name="endDate"
                    sx={{ ml: 3, width: 200 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={this.handleChange}
                  />
                  {!isLoading && (
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ ml: 3 }}
                      onClick={this.fetchData}
                    >
                      Reload
                    </Button>
                  )}
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              {inventoryEvents && (
                <MUIDataTable
                  title={getTitleFromData('Inventory Audit Report', inventoryEvents)}
                  columns={this.columns}
                  data={formatTableData(inventoryEvents)}
                  options={this.tableOptions}
                />
              )}
            </Grid>
          </Grid>
        </Paper>
      </WrappedSpinner>
    );
  }
}

const mapStateToProps = ({ markets, reports }) => ({
  reportsLoading: reports.isLoading,
  inventoryEvents: get(reports, 'inventoryEvents', []),
  marketsLoading: markets.isLoading,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchInventoryReport: reportActions.fetchInventoryReport,
}, dispatch);

const InventoryReport = saveLocalSettingsWrapper(
  withPersistentSettings(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(withRouter(BaseInventoryReport)),
    LOCAL_STORAGE_KEY,
  ),
  LOCAL_STORAGE_KEY,
);

export default InventoryReport;
